import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { Loader2 } from "lucide-react"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { User, VerSehatKelompok } from "@/types"
import Combobox from "@/components/Combobox"
import InputError from "@/components/InputError"
import { getListUser } from "@/services/getData"
import { FaUserCheck } from "react-icons/fa6"

const patchDataAssignVerifikator = async (newData: {
  id_versehat_kelompok: string
  user_verifikator1: string
}) => {
  const response = await api.patch(
    "/setting/verifikator-surat-keterangan-sehat/asign-user",
    newData
  )
  return response.data
}

export default function Form({
  closeModal,
  selectedRow,
}: {
  closeModal: () => void
  selectedRow: VerSehatKelompok | null
}) {
  const [searchQueryVerifikator, setSearchQueryVerifikator] = useState("")
  const [openListVerifikator, setOpenListVerifikator] = useState(false)
  const [form, setForm] = useState({
    id_versehat_kelompok: selectedRow?.id_versehat_kelompok || "",
    user_verifikator1: "",
  })

  const { data: dataVerifikator, isPending: isPendingDataVerifikator } =
    useQuery({
      queryKey: ["listVerifikator", searchQueryVerifikator],
      queryFn: () =>
        getListUser(searchQueryVerifikator, 10, "verifikator_sehat"),
      select: (data) =>
        data?.data &&
        data.data?.map((user: User) => ({
          label: user.nama_lengkap,
          value: `${user.id}`,
        })),
    })

  const queryClient = useQueryClient()
  const { mutate, isPending, error } = useMutation({
    mutationFn: patchDataAssignVerifikator,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listKelompokVerifikasiPesertaSuratKeteranganSehat"],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={<FaUserCheck />}
        title={`Daftarkan Verifikator ke kelompok ${selectedRow?.kelompok}`}
        description="Mendaftarkan verifikator ke kelompok yang dipilih."
      />
      <div>
        <Label>Verifikator</Label>
        <Combobox
          serverSide
          open={openListVerifikator}
          setOpen={setOpenListVerifikator}
          value={form.user_verifikator1}
          setValue={(newValue) => {
            setForm((prev) => ({
              ...prev,
              user_verifikator1: newValue,
            }))
          }}
          options={dataVerifikator}
          placeholder="Pilih Verifikator"
          onChange={(event) => setSearchQueryVerifikator(event.target.value)}
          searchQueryValue={searchQueryVerifikator || ""}
          isPending={isPendingDataVerifikator}
        />
        <InputError
          text={error?.response?.data.errors?.user_verifikator1?.join(",")}
        />
      </div>

      <div className="mt-3 flex gap-3">
        <Button
          type="button"
          variant={"outline"}
          disabled={isPending}
          onClick={() => closeModal()}
        >
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Daftarkan"
          )}
        </Button>
      </div>
    </form>
  )
}
